// Raw event timeline view for the Phoebe chat debug panels: a virtualized
// event list with expandable payloads, plus a toggleable tool-call histogram.
import ReactJson from '@microlink/react-json-view';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
    Activity,
    BarChart3,
    ChevronDown,
    ChevronRight,
    Circle,
    CircleCheck,
    Database,
    Layers,
    List,
    type LucideIcon,
    MessageSquare,
    Play,
    Wrench,
} from 'lucide-react';
import {
    type ComponentType,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    consumeDebugTimelineFocus,
    type PhoebeChatDebugFocusTarget,
    usePhoebeChatDebugTimelineFocus,
} from '@apps/web/components/phoebe_chat_debug_focus';
import {
    getCompactionSummaryText,
    getTimelineEntryCommand,
    getTimelineEntryLabel,
    getTimelineEntryToolName,
    type PhoebeChatDebugSnapshot,
    type PhoebeChatDebugTimelineEntry,
    shortId,
} from '@apps/web/components/phoebe_chat_debug_snapshot';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { formatElapsedMs } from '@apps/web/lib/format_elapsed';
import { useTheme } from '@apps/web/lib/theme_store';

function formatRecordCount(count: number, label: string): string {
    return count === 1 ? `1 ${label} record` : `${count} ${label} records`;
}

function formatTimestamp(value: string): string {
    const timestamp = Date.parse(value);
    if (!Number.isFinite(timestamp)) {
        return value;
    }
    return new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
    }).format(timestamp);
}

function getEntryElapsedLabel(
    entry: PhoebeChatDebugTimelineEntry,
    startTimeMs: number | null,
): string | null {
    if (startTimeMs === null) {
        return null;
    }
    const timestamp = Date.parse(entry.createdAt);
    if (!Number.isFinite(timestamp)) {
        return null;
    }
    return formatElapsedMs(timestamp - startTimeMs);
}

function isActivePendingTimelineEntry(
    entry: PhoebeChatDebugTimelineEntry,
): boolean {
    return entry.source === 'pending' && entry.status === 'pending';
}

function getTimelineEntryBadgeLabel(
    entry: PhoebeChatDebugTimelineEntry,
): string {
    if (entry.eventOffset !== null) {
        return `#${entry.eventOffset}`;
    }

    if (entry.source !== 'pending') {
        return entry.source;
    }

    if (entry.status === 'pending') {
        return 'queued';
    }
    if (entry.status === 'drained' || entry.status === 'rejected') {
        return entry.status;
    }
    return 'queue record';
}

function getTimelineEntryKey(entry: PhoebeChatDebugTimelineEntry): string {
    return `${entry.source}-${entry.id}`;
}

function getTimelineEntryStatusLabel(
    entry: PhoebeChatDebugTimelineEntry,
): string | null {
    if (entry.source === 'pending') {
        return entry.status === 'pending' ||
            entry.status === 'drained' ||
            entry.status === 'rejected'
            ? null
            : entry.status;
    }
    return entry.status;
}

function getTimelineEntryIcon(entry: PhoebeChatDebugTimelineEntry): LucideIcon {
    if (
        getTimelineEntryToolName(entry) ||
        entry.type === 'tool_call' ||
        entry.type === 'tool_output'
    ) {
        return Wrench;
    }
    switch (entry.type) {
        case 'user_message':
        case 'assistant_text':
        case 'assistant_reasoning':
            return MessageSquare;
        case 'status_update':
            return Activity;
        case 'run_started':
            return Play;
        case 'run_completed':
            return CircleCheck;
        case 'item_compaction':
            return Layers;
        default:
            return Circle;
    }
}

function RawTimelineEntry({
    entry,
    startTimeMs,
    isExpanded,
    onToggle,
}: {
    readonly entry: PhoebeChatDebugTimelineEntry;
    readonly startTimeMs: number | null;
    readonly isExpanded: boolean;
    readonly onToggle: () => void;
}) {
    const statusLabel = getTimelineEntryStatusLabel(entry);
    const toolName = getTimelineEntryToolName(entry);
    const command = getTimelineEntryCommand(entry);
    const elapsedLabel = getEntryElapsedLabel(entry, startTimeMs);
    const EntryIcon = getTimelineEntryIcon(entry);
    const ExpandIcon = isExpanded ? ChevronDown : ChevronRight;
    return (
        <div className="border-border/70 border-b py-2.5">
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={isExpanded}
                className="flex w-full cursor-pointer items-start gap-2 text-left"
            >
                <ExpandIcon
                    aria-hidden="true"
                    className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0"
                />
                <Badge
                    variant={entry.source === 'event' ? 'secondary' : 'outline'}
                    className="mt-0.5 h-5 rounded-sm px-1.5 text-[10px] font-medium"
                >
                    {getTimelineEntryBadgeLabel(entry)}
                </Badge>
                <EntryIcon
                    aria-hidden="true"
                    className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0"
                />
                <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-sans text-xs">
                            {getTimelineEntryLabel(entry)}
                        </span>
                        {toolName ? (
                            <span className="truncate font-sans text-xs font-semibold">
                                {toolName}
                            </span>
                        ) : null}
                        {command ? (
                            <span
                                className="text-muted-foreground min-w-0 truncate font-mono text-[10px]"
                                title={command}
                            >
                                {`$ ${command}`}
                            </span>
                        ) : null}
                        {statusLabel ? (
                            <span className="text-muted-foreground text-[10px]">
                                {statusLabel}
                            </span>
                        ) : null}
                        {elapsedLabel ? (
                            <span className="text-muted-foreground ml-auto shrink-0 text-[10px]">
                                {elapsedLabel}
                            </span>
                        ) : null}
                    </div>
                    <div className="text-muted-foreground mt-0.5 text-[10px]">
                        {formatTimestamp(entry.createdAt)} | {shortId(entry.id)}
                    </div>
                </div>
            </button>
            {isExpanded ? <RawTimelineEntryDetails entry={entry} /> : null}
        </div>
    );
}

// Rendered only for expanded rows: mounting a JSON tree for every payload
// eagerly is what made large timelines unusably slow.
function RawTimelineEntryDetails({
    entry,
}: {
    readonly entry: PhoebeChatDebugTimelineEntry;
}) {
    const compactionSummaryText = getCompactionSummaryText(entry);
    const theme = useTheme();
    const isDark =
        theme === 'dark' ||
        (theme === 'system' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-color-scheme: dark)').matches);
    return (
        <div>
            {compactionSummaryText ? (
                <div className="bg-background mt-2 rounded-sm border p-2">
                    <div className="text-muted-foreground mb-1 text-[10px] font-medium">
                        Compaction summary
                    </div>
                    <div className="max-h-64 overflow-auto text-[11px] leading-relaxed whitespace-pre-wrap">
                        {compactionSummaryText}
                    </div>
                </div>
            ) : null}
            <div className="bg-muted/60 mt-2 overflow-x-auto rounded-sm p-2">
                <ReactJson
                    src={entry.raw}
                    name={false}
                    collapsed={2}
                    enableClipboard={false}
                    displayDataTypes={false}
                    displayObjectSize={false}
                    quotesOnKeys={false}
                    theme={isDark ? 'monokai' : 'rjv-default'}
                    style={{
                        backgroundColor: 'transparent',
                        fontFamily:
                            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        fontSize: '10px',
                        lineHeight: 1.45,
                    }}
                />
            </div>
        </div>
    );
}

function ToolCallHistogram({
    timeline,
}: {
    readonly timeline: Array<PhoebeChatDebugTimelineEntry>;
}) {
    const toolCounts = useMemo(() => {
        const counts = new Map<string, number>();
        for (const entry of timeline) {
            if (entry.type !== 'tool_call') {
                continue;
            }
            const toolName = getTimelineEntryToolName(entry) ?? '(unknown)';
            counts.set(toolName, (counts.get(toolName) ?? 0) + 1);
        }
        return Array.from(counts, ([toolName, count]) => ({
            toolName,
            count,
        })).sort(
            (left, right) =>
                right.count - left.count ||
                left.toolName.localeCompare(right.toolName),
        );
    }, [timeline]);

    if (toolCounts.length === 0) {
        return (
            <div className="border-border/70 text-muted-foreground border-b py-3 text-xs">
                No tool calls yet.
            </div>
        );
    }

    const maxCount = toolCounts[0]?.count ?? 1;
    return (
        <div className="py-1">
            {toolCounts.map(({ toolName, count }) => (
                <div key={toolName} className="flex items-center gap-2 py-1">
                    <span
                        className="w-44 shrink-0 truncate text-right font-sans text-xs"
                        title={toolName}
                    >
                        {toolName}
                    </span>
                    <div className="min-w-0 flex-1">
                        <div
                            className="bg-primary/70 h-3 rounded-sm"
                            style={{
                                width: `${(count / maxCount) * 100}%`,
                                minWidth: '2px',
                            }}
                        />
                    </div>
                    <span className="text-muted-foreground w-8 shrink-0 text-xs">
                        {count}
                    </span>
                </div>
            ))}
        </div>
    );
}

export function PhoebeChatDebugEventTimeline({
    focusTarget,
    snapshot,
    entryComponent: EntryComponent = RawTimelineEntry,
    title = 'Raw Event Timeline',
    initialScrollPosition = 'start',
    isLoadingHistory = false,
    hasOlderHistory = false,
    isLoadingOlderHistory = false,
    historyError = null,
    onReachOlderHistory,
    onRetryHistory,
}: {
    readonly title?: string;
    readonly entryComponent?: ComponentType<{
        readonly entry: PhoebeChatDebugTimelineEntry;
        readonly startTimeMs: number | null;
        readonly isExpanded: boolean;
        readonly onToggle: () => void;
    }>;
    readonly focusTarget?: PhoebeChatDebugFocusTarget;
    readonly snapshot: PhoebeChatDebugSnapshot | null;
    readonly initialScrollPosition?: 'start' | 'end';
    readonly isLoadingHistory?: boolean;
    readonly hasOlderHistory?: boolean;
    readonly isLoadingOlderHistory?: boolean;
    readonly historyError?: Error | null;
    readonly onReachOlderHistory?: () => void;
    readonly onRetryHistory?: () => void;
}) {
    // Derived once per snapshot, not per render: the virtualizer re-renders
    // this component on every scroll.
    const {
        visibleTimeline,
        startTimeMs,
        eventCount,
        activePendingCount,
        rejectedPendingCount,
        otherQueueRecordCount,
    } = useMemo(() => {
        const timeline = snapshot?.timeline ?? [];
        // Entries are ordered by event offset, which can disagree with
        // wall-clock order, so T+0 is the earliest timestamp, not the first
        // entry.
        let earliestTimeMs: number | null = null;
        for (const entry of timeline) {
            const timestamp = Date.parse(entry.createdAt);
            if (
                Number.isFinite(timestamp) &&
                (earliestTimeMs === null || timestamp < earliestTimeMs)
            ) {
                earliestTimeMs = timestamp;
            }
        }
        return {
            startTimeMs: earliestTimeMs,
            visibleTimeline: timeline.filter(
                (entry) =>
                    !(entry.source === 'pending' && entry.status === 'drained'),
            ),
            eventCount: timeline.filter((entry) => entry.source === 'event')
                .length,
            activePendingCount: timeline.filter(isActivePendingTimelineEntry)
                .length,
            rejectedPendingCount: timeline.filter(
                (entry) =>
                    entry.source === 'pending' && entry.status === 'rejected',
            ).length,
            otherQueueRecordCount: timeline.filter(
                (entry) =>
                    entry.source === 'pending' &&
                    entry.status !== 'pending' &&
                    entry.status !== 'drained' &&
                    entry.status !== 'rejected',
            ).length,
        };
    }, [snapshot]);

    const [view, setView] = useState<'timeline' | 'histogram'>('timeline');

    // Expansion state lives here (not in row components) because virtualized
    // rows unmount when scrolled out of view.
    const [expandedEntryKeys, setExpandedEntryKeys] = useState<
        ReadonlySet<string>
    >(new Set());
    const toggleEntryExpanded = useCallback((entryKey: string) => {
        setExpandedEntryKeys((previous) => {
            const next = new Set(previous);
            if (next.has(entryKey)) {
                next.delete(entryKey);
            } else {
                next.add(entryKey);
            }
            return next;
        });
    }, []);

    const scrollElementRef = useRef<HTMLDivElement | null>(null);
    const scrollAnchorRef = useRef<{
        readonly runId: PhoebeChatDebugSnapshot['phoebeRunId'];
        readonly entryKey: string;
        readonly entryIndex: number;
        readonly offsetFromEntryStart: number;
    } | null>(null);
    const initialScrolledRunIdRef = useRef<string | null>(null);
    const userScrollActiveRef = useRef(false);
    const userScrolledTowardStartRef = useRef(false);
    const previousScrollTopRef = useRef(0);
    const previousTouchYRef = useRef<number | null>(null);
    const virtualizer = useVirtualizer({
        count: visibleTimeline.length,
        getScrollElement: () => scrollElementRef.current,
        estimateSize: () => 52,
        overscan: 10,
        getItemKey: (index) => {
            const entry = visibleTimeline[index];
            return entry ? getTimelineEntryKey(entry) : index;
        },
    });
    const debugTimelineFocus = usePhoebeChatDebugTimelineFocus();

    const captureScrollAnchor = useCallback(() => {
        const scrollElement = scrollElementRef.current;
        if (!scrollElement || !snapshot) {
            return;
        }
        const anchorItem = virtualizer.getVirtualItemForOffset(
            scrollElement.scrollTop,
        );
        const anchorEntry = anchorItem
            ? visibleTimeline[anchorItem.index]
            : undefined;
        if (!anchorItem || !anchorEntry) {
            scrollAnchorRef.current = null;
            return;
        }
        scrollAnchorRef.current = {
            runId: snapshot.phoebeRunId,
            entryKey: getTimelineEntryKey(anchorEntry),
            entryIndex: anchorItem.index,
            offsetFromEntryStart: scrollElement.scrollTop - anchorItem.start,
        };
    }, [snapshot, virtualizer, visibleTimeline]);

    useLayoutEffect(() => {
        const anchor = scrollAnchorRef.current;
        const scrollElement = scrollElementRef.current;
        if (!anchor || !scrollElement) {
            return;
        }
        if (anchor.runId !== snapshot?.phoebeRunId) {
            scrollAnchorRef.current = null;
            return;
        }
        const anchorIndex = visibleTimeline.findIndex(
            (entry) => getTimelineEntryKey(entry) === anchor.entryKey,
        );
        if (anchorIndex < 0) {
            scrollAnchorRef.current = null;
            return;
        }
        if (anchorIndex <= anchor.entryIndex) {
            return;
        }
        const anchorOffset = virtualizer.getOffsetForIndex(
            anchorIndex,
            'start',
        )?.[0];
        if (anchorOffset === undefined) {
            return;
        }
        userScrollActiveRef.current = false;
        userScrolledTowardStartRef.current = false;
        scrollElement.scrollTop = anchorOffset + anchor.offsetFromEntryStart;
        previousScrollTopRef.current = scrollElement.scrollTop;
        scrollAnchorRef.current = null;
    }, [snapshot?.phoebeRunId, visibleTimeline, virtualizer]);

    useLayoutEffect(() => {
        const runId = snapshot?.phoebeRunId;
        if (
            initialScrollPosition !== 'end' ||
            !runId ||
            visibleTimeline.length === 0 ||
            initialScrolledRunIdRef.current === runId
        ) {
            return;
        }
        userScrollActiveRef.current = false;
        userScrolledTowardStartRef.current = false;
        virtualizer.scrollToIndex(visibleTimeline.length - 1, { align: 'end' });
        initialScrolledRunIdRef.current = runId;
    }, [
        initialScrollPosition,
        snapshot?.phoebeRunId,
        virtualizer,
        visibleTimeline.length,
    ]);

    const loadOlderFromUserScroll = useCallback(
        (element: HTMLDivElement) => {
            if (
                view !== 'timeline' ||
                !hasOlderHistory ||
                isLoadingOlderHistory ||
                historyError !== null ||
                !onReachOlderHistory
            ) {
                return;
            }
            if (element.scrollTop <= 1) {
                userScrollActiveRef.current = false;
                userScrolledTowardStartRef.current = false;
                captureScrollAnchor();
                onReachOlderHistory();
            }
        },
        [
            captureScrollAnchor,
            hasOlderHistory,
            historyError,
            isLoadingOlderHistory,
            onReachOlderHistory,
            view,
        ],
    );

    const handleHistoryScroll = useCallback(
        (event: React.UIEvent<HTMLDivElement>) => {
            const element = event.currentTarget;
            const movedTowardStart =
                element.scrollTop < previousScrollTopRef.current;
            previousScrollTopRef.current = element.scrollTop;
            if (userScrollActiveRef.current && isLoadingOlderHistory) {
                captureScrollAnchor();
            }
            if (
                userScrollActiveRef.current &&
                userScrolledTowardStartRef.current &&
                movedTowardStart
            ) {
                loadOlderFromUserScroll(element);
            }
        },
        [captureScrollAnchor, isLoadingOlderHistory, loadOlderFromUserScroll],
    );

    const handleHistoryPointerMove = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (event.buttons > 0 || event.pointerType === 'touch') {
                userScrollActiveRef.current = true;
                userScrolledTowardStartRef.current = true;
            }
        },
        [],
    );

    useLayoutEffect(() => {
        userScrollActiveRef.current = false;
        userScrolledTowardStartRef.current = false;
        previousScrollTopRef.current = scrollElementRef.current?.scrollTop ?? 0;
    }, [snapshot?.phoebeRunId, view]);

    const handleHistoryWheel = useCallback(
        (event: React.WheelEvent<HTMLDivElement>) => {
            userScrollActiveRef.current = true;
            userScrolledTowardStartRef.current = event.deltaY < 0;
            if (event.deltaY < 0) {
                loadOlderFromUserScroll(event.currentTarget);
            }
        },
        [loadOlderFromUserScroll],
    );

    const handleHistoryKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            userScrollActiveRef.current = true;
            userScrolledTowardStartRef.current =
                event.key === 'ArrowUp' ||
                event.key === 'Home' ||
                event.key === 'PageUp';
            if (userScrolledTowardStartRef.current) {
                loadOlderFromUserScroll(event.currentTarget);
            }
        },
        [loadOlderFromUserScroll],
    );

    const handleHistoryTouchStart = useCallback(
        (event: React.TouchEvent<HTMLDivElement>) => {
            previousTouchYRef.current = event.touches[0]?.clientY ?? null;
            userScrollActiveRef.current = true;
            userScrolledTowardStartRef.current = false;
        },
        [],
    );

    const handleHistoryTouchMove = useCallback(
        (event: React.TouchEvent<HTMLDivElement>) => {
            const currentTouchY = event.touches[0]?.clientY;
            const previousTouchY = previousTouchYRef.current;
            if (currentTouchY === undefined || previousTouchY === null) {
                return;
            }
            userScrollActiveRef.current = true;
            userScrolledTowardStartRef.current = currentTouchY > previousTouchY;
            previousTouchYRef.current = currentTouchY;
            if (userScrolledTowardStartRef.current) {
                loadOlderFromUserScroll(event.currentTarget);
            }
        },
        [loadOlderFromUserScroll],
    );

    useEffect(() => {
        if (
            debugTimelineFocus.eventId === null ||
            debugTimelineFocus.requestId === 0 ||
            (debugTimelineFocus.runId !== null &&
                debugTimelineFocus.runId !== snapshot?.phoebeRunId) ||
            (debugTimelineFocus.target !== null &&
                debugTimelineFocus.target !== focusTarget)
        ) {
            return;
        }
        const index = visibleTimeline.findIndex(
            (entry) =>
                entry.source === 'event' &&
                entry.id === debugTimelineFocus.eventId,
        );
        if (index < 0) {
            return;
        }
        if (view !== 'timeline') {
            setView('timeline');
            return;
        }
        const entry = visibleTimeline[index];
        if (entry === undefined) {
            return;
        }
        const entryKey = getTimelineEntryKey(entry);
        setExpandedEntryKeys((previous) => {
            if (previous.has(entryKey)) {
                return previous;
            }
            return new Set([...previous, entryKey]);
        });
        userScrollActiveRef.current = false;
        userScrolledTowardStartRef.current = false;
        virtualizer.scrollToIndex(index, { align: 'center' });
        consumeDebugTimelineFocus(debugTimelineFocus.requestId);
    }, [
        debugTimelineFocus.eventId,
        debugTimelineFocus.requestId,
        debugTimelineFocus.runId,
        debugTimelineFocus.target,
        focusTarget,
        snapshot?.phoebeRunId,
        visibleTimeline,
        virtualizer,
        view,
    ]);

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className="px-3 pt-2">
                <div className="mb-1 flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-semibold">
                        <Database className="h-3.5 w-3.5" />
                        {title}
                    </div>
                    <div className="flex gap-0.5">
                        <Button
                            type="button"
                            size="icon"
                            variant={
                                view === 'timeline' ? 'secondary' : 'ghost'
                            }
                            onClick={() => setView('timeline')}
                            aria-label="Show event timeline"
                            aria-pressed={view === 'timeline'}
                            className="h-6 w-6"
                        >
                            <List className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            type="button"
                            size="icon"
                            variant={
                                view === 'histogram' ? 'secondary' : 'ghost'
                            }
                            onClick={() => setView('histogram')}
                            aria-label="Show tool call histogram"
                            aria-pressed={view === 'histogram'}
                            className="h-6 w-6"
                        >
                            <BarChart3 className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                </div>
                <div className="text-muted-foreground mb-1 text-[11px]">
                    {visibleTimeline.length} entries | {eventCount} events |{' '}
                    {formatRecordCount(activePendingCount, 'queued')}
                    {rejectedPendingCount > 0
                        ? ` | ${formatRecordCount(rejectedPendingCount, 'rejected')}`
                        : ''}
                    {otherQueueRecordCount > 0
                        ? ` | ${formatRecordCount(otherQueueRecordCount, 'queue')}`
                        : ''}
                </div>
                {visibleTimeline.length > 0 &&
                (hasOlderHistory || historyError) ? (
                    <div className="flex min-h-7 items-center justify-center pb-1">
                        {isLoadingOlderHistory ? (
                            <span className="text-muted-foreground text-xs">
                                Loading older events…
                            </span>
                        ) : historyError ? (
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                    captureScrollAnchor();
                                    onRetryHistory?.();
                                }}
                            >
                                Try again
                            </Button>
                        ) : (
                            <span className="text-muted-foreground text-xs">
                                Scroll to the top for older events
                            </span>
                        )}
                    </div>
                ) : null}
            </div>
            <div
                ref={scrollElementRef}
                className="min-h-0 flex-1 overflow-y-auto px-3 pb-4"
                role="region"
                aria-label="Event history"
                tabIndex={0}
                onScroll={handleHistoryScroll}
                onScrollEnd={() => {
                    userScrollActiveRef.current = false;
                    userScrolledTowardStartRef.current = false;
                }}
                onWheel={handleHistoryWheel}
                onKeyDown={handleHistoryKeyDown}
                onPointerDown={() => {
                    userScrollActiveRef.current = true;
                    userScrolledTowardStartRef.current = true;
                }}
                onPointerMove={handleHistoryPointerMove}
                onTouchStart={handleHistoryTouchStart}
                onTouchMove={handleHistoryTouchMove}
            >
                {view === 'histogram' ? (
                    <ToolCallHistogram timeline={visibleTimeline} />
                ) : visibleTimeline.length ? (
                    <div
                        className="relative w-full"
                        style={{ height: virtualizer.getTotalSize() }}
                    >
                        {virtualizer.getVirtualItems().map((virtualItem) => {
                            const entry = visibleTimeline[virtualItem.index];
                            if (!entry) {
                                return null;
                            }
                            const entryKey = getTimelineEntryKey(entry);
                            return (
                                <div
                                    key={virtualItem.key}
                                    data-index={virtualItem.index}
                                    ref={virtualizer.measureElement}
                                    className="absolute top-0 left-0 w-full"
                                    style={{
                                        transform: `translateY(${virtualItem.start}px)`,
                                    }}
                                >
                                    <EntryComponent
                                        entry={entry}
                                        startTimeMs={startTimeMs}
                                        isExpanded={expandedEntryKeys.has(
                                            entryKey,
                                        )}
                                        onToggle={() =>
                                            toggleEntryExpanded(entryKey)
                                        }
                                    />
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="border-border/70 text-muted-foreground border-b py-3 text-xs">
                        {isLoadingHistory
                            ? 'Loading event history…'
                            : historyError
                              ? 'Couldn’t load event history.'
                              : 'No event feed available.'}
                        {historyError && onRetryHistory ? (
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="ml-2"
                                onClick={onRetryHistory}
                            >
                                Try again
                            </Button>
                        ) : null}
                    </div>
                )}
            </div>
        </div>
    );
}
